// API client utility for making authenticated requests

const API_BASE_URL = '/api';

interface RequestOptions extends RequestInit {
    params?: Record<string, string>;
}

async function request<T>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE_URL}${endpoint}`;

    if (params) {
        const queryString = new URLSearchParams(params).toString();
        url += `?${queryString}`;
    }

    const response = await fetch(url, {
        ...fetchOptions,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...fetchOptions.headers,
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
}

export const api = {
    // Users
    getCurrentUser: () => request<any>('/users/me'),
    updateUser: (data: any) => request<any>('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),

    // Organizations
    getOrganizations: () => request<any[]>('/organizations'),
    createOrganization: (data: any) => request<any>('/organizations', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    getOrganization: (id: string) => request<any>(`/organizations/${id}`),
    updateOrganization: (id: string, data: any) => request<any>(`/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteOrganization: (id: string) => request<any>(`/organizations/${id}`, {
        method: 'DELETE',
    }),
    getOrgMembers: (id: string) => request<any[]>(`/organizations/${id}/members`),
    addOrgMember: (id: string, data: any) => request<any>(`/organizations/${id}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateOrgMember: (orgId: string, userId: string, data: any) =>
        request<any>(`/organizations/${orgId}/members/${userId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }),
    removeOrgMember: (orgId: string, userId: string) =>
        request<any>(`/organizations/${orgId}/members/${userId}`, {
            method: 'DELETE',
        }),

    // Scripts
    getScripts: (params?: any) => request<any[]>('/scripts', { params }),
    createScript: (data: any) => request<any>('/scripts', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    getScript: (id: string) => request<any>(`/scripts/${id}`),
    updateScript: (id: string, data: any) => request<any>(`/scripts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteScript: (id: string) => request<any>(`/scripts/${id}`, {
        method: 'DELETE',
    }),
    getScriptPermissions: (id: string) => request<any[]>(`/scripts/${id}/permissions`),
    grantScriptPermission: (id: string, data: any) =>
        request<any>(`/scripts/${id}/permissions`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    revokeScriptPermission: (scriptId: string, userId: string) =>
        request<any>(`/scripts/${scriptId}/permissions/${userId}`, {
            method: 'DELETE',
        }),

    // Cloud Connectors
    getConnectors: () => request<any[]>('/connectors'),
    connectNextcloud: (data: any) => request<any>('/connectors/nextcloud', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    disconnectConnector: (id: string) => request<any>(`/connectors/${id}`, {
        method: 'DELETE',
    }),
    syncScript: (scriptId: string, data: any) => request<any>(`/sync/${scriptId}`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    // Export/Import
    exportScript: (id: string) =>
        fetch(`${API_BASE_URL}/scripts/${id}/export`, {
            credentials: 'include',
        }).then(res => {
            if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
            return res.blob();
        }),

    importScript: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return fetch(`${API_BASE_URL}/scripts/import`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        }).then(res => res.json());
    },
};
